import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getImageUrl } from '../utils/imageHelper';

/**
 * Hook personalizado para obtener productos de Firestore SIN procesar imágenes
 * Usado por AdminPanel para evitar duplicar rutas base
 * @param {string} category - Categoría a filtrar (opcional)
 * @returns {object} { products, loading, error }
 */
export const useProductsRaw = (category = null) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productsRef = collection(db, 'products');
        
        let q;
        if (category) {
          q = query(productsRef, where('category', '==', category));
        } else {
          q = query(productsRef);
        }

        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            firestoreId: doc.id,  // Guardar el documentId de Firestore por separado
            // El 'id' del JSON se preserva tal como está
          };
        });

        setProducts(productsData);
        setError(null);
      } catch (err) {
        console.error('Error al obtener productos:', err);
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  return { products, loading, error };
};

/**
 * Hook personalizado para obtener productos de Firestore CON imágenes procesadas
 * @param {string} category - Categoría a filtrar (opcional)
 * @returns {object} { products, loading, error }
 */
export const useProducts = (category = null) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productsRef = collection(db, 'products');
        
        let q;
        if (category) {
          q = query(productsRef, where('category', '==', category));
        } else {
          q = query(productsRef);
        }

        const snapshot = await getDocs(q);
        let productsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            firestoreId: doc.id,  // Guardar el documentId de Firestore por separado
            // El 'id' del JSON se preserva tal como está
          };
        });

        // Procesar los paths de imágenes
        productsData = productsData.map(product => {
          let processedImage = product.image;
          // Solo procesar si es una ruta local, no una URL https://
          if (product.image && !product.image.startsWith('https://') && !product.image.startsWith('http://')) {
            processedImage = getImageUrl(product.image);
          }
          
          return {
            ...product,
            image: processedImage,
            images: product.images ? product.images.map(img => {
              // No procesar URLs de YouTube
              if (img.includes('youtu')) return img;
              // No procesar URLs de Firebase Storage (ya son https://)
              if (img.startsWith('https://') || img.startsWith('http://')) return img;
              // Solo procesar rutas locales
              return getImageUrl(img);
            }) : product.images,
          };
        });

        setProducts(productsData);
        setError(null);
      } catch (err) {
        console.error('Error al obtener productos:', err);
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  return { products, loading, error };
};
